"use client";

import { useEffect, useState } from "react";
import Link from "next/link";

import { getCurrentTransits, type TransitPosition } from "@/lib/api/transits";
import { RASHI_MAP } from "@/lib/rashi";

/**
 * The sitewide "live gochar" strip.
 *
 * ## Why this fetches instead of rendering a constant
 *
 * This component previously shipped a module-level `LIVE_TRANSITS` array —
 * `{ graha: "Sun", sign: "Leo", degrees: "03°" }` and four more — as literal
 * source. It was labelled LIVE and rendered on every app page. Those are
 * astronomical facts, so rules 24 and 29 put them with the engine and forbid
 * them as literals anywhere else: the Moon changes rashi every ~2.3 days, so
 * the strip was publishing a wrong Moon sign most of the time, under a pulsing
 * "LIVE" dot, on a YMYL surface.
 *
 * The values now come from the engine at runtime. There is deliberately NO
 * fallback array: if the engine cannot be reached the planetary row renders
 * nothing at all. A stale-but-plausible position is the failure mode that got
 * us here, and an empty row is strictly better than a confident wrong one.
 *
 * ## Why the strip is app-runtime only
 *
 * Statically exported artifacts have their JavaScript stripped and are carried
 * forward between releases, so a value baked into one would be frozen for as
 * long as that artifact lives. `INTERACTIVE_ONLY_CHROME` in `@/lib/site-chrome`
 * records this as a declared difference between the two chrome projections
 * rather than leaving it as accidental drift. The quick links below are chrome
 * and DO appear on both.
 */

/** Grahas the strip shows, in the order jyotish convention reads them. */
const RIBBON_GRAHAS: { id: string; label: string; icon: string }[] = [
  { id: "sun", label: "Sun", icon: "☀️" },
  { id: "moon", label: "Moon", icon: "🌙" },
  { id: "jupiter", label: "Jupiter", icon: "♃" },
  { id: "saturn", label: "Saturn", icon: "🪐" },
  { id: "rahu", label: "Rahu", icon: "☊" },
];

/**
 * `current_positions` is independent of the caller's moon rashi — the endpoint
 * needs one to score the personalised `active_transits` it also returns, which
 * this strip ignores. Mesh is the zero point of the zodiac and is used here as
 * that required-but-unused argument, never as a claim about the visitor.
 */
const POSITIONS_REFERENCE_RASHI = "mesh";

function rashiLabel(rashi: string): string {
  return RASHI_MAP[rashi]?.name_en ?? rashi;
}

export default function EphemerisTransitRibbon() {
  const [positions, setPositions] = useState<TransitPosition[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    getCurrentTransits(POSITIONS_REFERENCE_RASHI)
      .then((response) => {
        if (!cancelled) setPositions(response.current_positions);
      })
      .catch(() => {
        // Engine unreachable: show no positions rather than invented ones.
        if (!cancelled) setPositions([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const shown = (positions ?? [])
    .filter((p) => RIBBON_GRAHAS.some((g) => g.id === p.planet_id))
    .sort(
      (a, b) =>
        RIBBON_GRAHAS.findIndex((g) => g.id === a.planet_id) -
        RIBBON_GRAHAS.findIndex((g) => g.id === b.planet_id)
    );

  return (
    <div
      className="border-b border-border-default bg-surface-raised py-1.5 px-3 text-caption overflow-x-auto scrollbar-none"
      data-design-owner="ephemeris-ribbon"
    >
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-4">
        {/* Reserve the row height whether or not positions have arrived, so the
            strip never grows on hydration and pushes the page down (rule 58). */}
        <div className="flex min-h-[18px] items-center gap-4 shrink-0">
          <div className="flex items-center gap-1.5 font-bold text-intent-brand shrink-0">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-intent-brand animate-ping" />
            <span>LIVE GOCHAR:</span>
          </div>

          <div className="flex items-center gap-3 text-text-secondary">
            {shown.map((position) => {
              const graha = RIBBON_GRAHAS.find((g) => g.id === position.planet_id);
              return (
                <span
                  key={position.planet_id}
                  className="inline-flex items-center gap-1 shrink-0 font-medium"
                >
                  <span>{graha?.icon}</span>
                  <span className="text-text-primary font-semibold">{graha?.label}:</span>
                  <span>
                    {rashiLabel(position.rashi)} {Math.floor(position.rashi_degree)}&deg;
                  </span>
                  {position.is_retrograde && (
                    <span className="text-intent-brand font-bold text-caption">(R)</span>
                  )}
                </span>
              );
            })}
          </div>
        </div>

        {/* Quick links are chrome, not facts — they render regardless. */}
        <div className="hidden sm:flex items-center gap-3 shrink-0 text-text-muted">
          <Link
            href="/panchang/"
            className="font-medium text-intent-brand hover:underline transition"
          >
            Today&apos;s Panchang &rarr;
          </Link>
          <span>&middot;</span>
          <Link href="/shani-transit/" className="hover:text-text-primary transition">
            Shani Gochar Matrix
          </Link>
        </div>
      </div>
    </div>
  );
}
